import React, { useEffect, useMemo } from "react";
import MainNavigation from "../components/navigation/MainNavigation";
import MobileNavigation from "../components/navigation/MobileNavigation";
import { fetchPagerDutyOnCall } from "../store/slices/pagerDutyOnCall";
import { useAppDispatch } from "../store";
import { useSelector } from "react-redux";
import { CircleLoader } from "../icons/Loaders";
import "./PagerDutyOnCall.scss";

// Interface defining the structure of on-call person data
interface OnCallPerson {
  responsible: string;
  name: string;
  phone: string;
  startTime: string;
  endTime: string;
}

// Interface defining the complete PagerDuty response structure
interface PagerDutyData {
  current: {
    realtime: OnCallPerson[];
    insights: OnCallPerson[];
  };
  nextShift: {
    realtime: OnCallPerson[];
    insights: OnCallPerson[];
    startsAt: string;
    endsAt: string;
  };
  timestamp: string;
}

const PagerDutyOnCall = () => {
  const dispatch = useAppDispatch();
  const peopleOnCall = useSelector((state: any) => state.pagerDutyOnCall.data) as PagerDutyData;
  const isLoading = useSelector((state: any) => state.pagerDutyOnCall.isLoading);
  const isMobileNavigationShown = useSelector((state: any) => state.mobile.isNavigationOpen);

  // Fetch PagerDuty on-call data when component mounts
  useEffect(() => {
    dispatch(fetchPagerDutyOnCall());
  }, [dispatch]);

  // Format date string to readable format
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Prepare table data for current realtime on-call
  const currentRealtimeTableData = useMemo(() => {
    if (!peopleOnCall?.current?.realtime) return [];

    return peopleOnCall.current.realtime.map((person: OnCallPerson) => ({
      responsible: person.responsible,
      name: person.name,
      phone: person.phone,
      startTime: formatDate(person.startTime),
      endTime: formatDate(person.endTime)
    }));
  }, [peopleOnCall]);

  // Prepare table data for current insights on-call
  const currentInsightsTableData = useMemo(() => {
    if (!peopleOnCall?.current?.insights) return [];

    return peopleOnCall.current.insights.map((person: OnCallPerson) => ({
      responsible: person.responsible,
      name: person.name,
      phone: person.phone,
      startTime: formatDate(person.startTime),
      endTime: formatDate(person.endTime)
    }));
  }, [peopleOnCall]);

  // Prepare table data for next shift realtime
  const nextRealtimeTableData = useMemo(() => {
    if (!peopleOnCall?.nextShift?.realtime) return [];

    return peopleOnCall.nextShift.realtime.map((person: OnCallPerson) => ({
      responsible: person.responsible,
      name: person.name,
      phone: person.phone,
      startTime: formatDate(person.startTime),
      endTime: formatDate(person.endTime)
    }));
  }, [peopleOnCall]);

  // Prepare table data for next shift insights
  const nextInsightsTableData = useMemo(() => {
    if (!peopleOnCall?.nextShift?.insights) return [];

    return peopleOnCall.nextShift.insights.map((person: OnCallPerson) => ({
      responsible: person.responsible,
      name: person.name,
      phone: person.phone,
      startTime: formatDate(person.startTime),
      endTime: formatDate(person.endTime)
    }));
  }, [peopleOnCall]);

  // Reusable function to render on-call person information
  const renderOnCallPerson = (person: any, index: number, sectionType: string) => (
    <div key={index}>
      <h3>
        {sectionType} - {person.responsible}
      </h3>
      <div>
        <strong>{person.name}</strong>
      </div>
      <div>
        <a href={`tel:${person.phone}`}>{person.phone}</a>
      </div>
    </div>
  );

  // Reusable function to render section content with loading state
  const renderSection = (data: any[], noDataMessage: string, sectionType:string) => {
    if (!isLoading) {
      return data.length > 0 ? (
        <div>
          {data.map((person, index) => renderOnCallPerson(person, index, sectionType))}
        </div>
      ) : (
        <div>{noDataMessage}</div>
      );
    } else {
      return (
        <div className="loader-container">
          <CircleLoader />
        </div>
      );
    }
  };

  return (
    <div>
      <MainNavigation />
      <MobileNavigation title="" />
      <div className={`pagerduty-page-container ${isMobileNavigationShown ? "mobile-menu-shown" : ""}`}>
        <h1>PagerDuty On-Call Schedule</h1>

        <div className="pagerduty-page-content">
          <div className="pagerduty-column">
            <h2>Current On-Call</h2>

            {/* Realtime Current */}
            <div>
              {renderSection(currentRealtimeTableData, "No current realtime on-call data available", "Realtime")}
            </div>

            {/* Insights Current */}
            <div>
              {renderSection(currentInsightsTableData, "No current insights on-call data available", "Insights")}
            </div>
          </div>

          {/* Next Shift Section */}
          <div className="pagerduty-column">
            <h2><em>Next Shift {peopleOnCall?.nextShift?.startsAt && (
                 <>from {formatDate(peopleOnCall.nextShift.startsAt)}</>
              )}</em></h2>

            {/* Realtime Next */}
            <div>
              {renderSection(nextRealtimeTableData, "No next shift realtime data available", "Realtime")}
            </div>

            {/* Insights Next */}
            <div>
              {renderSection(nextInsightsTableData, "No next shift insights data available", "Insights")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagerDutyOnCall;